import {useEffect, useReducer, ReactNode, useState} from 'react';
import {EntityContext} from './context';
import reducer from './reducer';
import {EntityContextActionType, EntityContextState} from './types';
import {useFetchAllItems} from '../../hooks/fetch-all-items';
import {EntitySocialResponsesRepository} from '../../repositories/entity-social-responses';
import {useAuth} from '../auth/context';
import {EntityPendingTasksRepository} from '../../repositories/entity-pending-tasks';
import {useOffice} from '../office/context';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {EntityOccurrencesRepository} from '../../repositories/entity-occurrences';
import eventEmitter from '../../events/event-emitter';
import {PushNotificationReceived, PUSH_NOTIFICATION_RECEIVED} from '../../events/event-types';
import {NotificationType} from '../../backend/enums/notification-type';

const initialState: EntityContextState = {
  availableSocialResponses: [],
  userEntitySocialResponses: [],
  pendingTasks: [],
  occurrencesCount: 0,
};

export const EntityContextProvider = ({children}: {children: ReactNode}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [showPending, setShowPending] = useState(false);
  const [hidePending, setHidePending] = useState(false);
  const {state: authState, hasReadPermission} = useAuth();
  const {showPendingTasks} = useOffice();
  const canReadPlanner = hasReadPermission(RolePermissionType.Calendar);
  const {items: entitySocialResponses, isLoading} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return EntitySocialResponsesRepository.instance.getEntitySocialResponses(
        authState.session?.selectedAffiliation?.entity.id,
        limit,
        skip
      );
    },
    enabled: authState.session?.selectedAffiliation !== undefined,
    deps: [authState.session?.selectedAffiliation?.entity.id],
  });

  useEffect(() => {
    if (showPending) {
      showPendingTasks(true);
      setShowPending(false);
    }
  }, [showPending, showPendingTasks]);

  useEffect(() => {
    if (hidePending) {
      showPendingTasks(false);
      setHidePending(false);
    }
  }, [hidePending, showPendingTasks]);

  useEffect(() => {
    if (authState?.session?.currentUser.id && authState.session?.selectedAffiliation?.entity.id) {
      EntitySocialResponsesRepository.instance
        .getUserSocialResponses(
          authState.session.selectedAffiliation.entity.id,
          authState.session.currentUser.id
        )
        .then((response) => {
          dispatch({
            type: EntityContextActionType.UPDATE_USER_ENTITY_SOCIAL_RESPONSES,
            userEntitySocialResponses: response,
          });
        });
    }
  }, [authState.session?.currentUser.id, authState.session?.selectedAffiliation?.entity.id]);

  useEffect(() => {
    const selectedAffiliation = authState.session?.selectedAffiliation;

    if (!selectedAffiliation || !canReadPlanner) {
      dispatch({
        type: EntityContextActionType.UPDATE_ENTITY_PENDING_TASKS,
        pendingTasks: [],
      });
      setHidePending(true);
    } else {
      EntityPendingTasksRepository.instance
        .getPendingTasks(selectedAffiliation.entity.id, 20, 0)
        .then((response) => {
          dispatch({
            type: EntityContextActionType.UPDATE_ENTITY_PENDING_TASKS,
            pendingTasks: response.data,
          });

          if (response.totalCount > 0 && response.data.length > 0) {
            setShowPending(true);
          } else {
            setHidePending(true);
          }
        })
        .catch((error) => {
          console.debug('🔴 Could not fetch entity pending tasks.', error);
          dispatch({
            type: EntityContextActionType.UPDATE_ENTITY_PENDING_TASKS,
            pendingTasks: [],
          });
          setHidePending(true);
        });
    }

    if (selectedAffiliation && hasReadPermission(RolePermissionType.Occurrences)) {
      EntityOccurrencesRepository.instance
        .getOccurrencesCount(selectedAffiliation.entity.id)
        .then((response) => {
          dispatch({
            type: EntityContextActionType.UPDATE_ENTITY_OCCURRENCES_COUNT,
            occurrencesCount: response,
          });
        });
      EntityOccurrencesRepository.instance
        .getOccurrencesTypes(selectedAffiliation.entity.id)
        .then((response) => {
          dispatch({
            type: EntityContextActionType.UPDATE_ENTITY_OCCURRENCES_TYPES,
            occurrencesTypes: response,
          });
        });
    }
  }, [authState.session?.selectedAffiliation, canReadPlanner, hasReadPermission]);

  useEffect(() => {
    if (entitySocialResponses) {
      dispatch({
        type: EntityContextActionType.UPDATE_AVAILABLE_ENTITY_SOCIAL_RESPONSES,
        availableSocialResponses: isLoading ? [] : entitySocialResponses,
      });
    }
  }, [entitySocialResponses, isLoading]);

  useEffect(() => {
    const selectedEntityId = authState.session?.selectedAffiliation?.entity.id;

    const onPushNotificationReceived = (notification: PushNotificationReceived) => {
      if (
        notification.notificationType !== NotificationType.NewOccurrence &&
        notification.notificationType !== NotificationType.NewOccurrenceAssigned
      ) {
        return;
      }

      const canReadOccurrences =
        hasReadPermission(RolePermissionType.Occurrences) ||
        hasReadPermission(RolePermissionType.DailyCareLog_Occurrences);

      if (!selectedEntityId || !canReadOccurrences) {
        return;
      }

      // Older service workers may not include entityId. Refresh in that case to avoid stale badges.
      if (notification.entityId && notification.entityId !== selectedEntityId) {
        return;
      }

      EntityOccurrencesRepository.instance
        .getOccurrencesCount(selectedEntityId)
        .then((response) => {
          console.debug('🔄 Occurrences badge count updated from push notification to', response);
          dispatch({
            type: EntityContextActionType.UPDATE_ENTITY_OCCURRENCES_COUNT,
            occurrencesCount: response,
          });
        });
    };

    eventEmitter.on(PUSH_NOTIFICATION_RECEIVED, onPushNotificationReceived);

    return () => {
      eventEmitter.off(PUSH_NOTIFICATION_RECEIVED, onPushNotificationReceived);
    };
  }, [authState.session?.selectedAffiliation?.entity.id, hasReadPermission]);

  return (
    <EntityContext.Provider
      value={{
        state,
        updateUserEntitySocialResponses: (socialResponseIds) => {
          const newUserSocialResponses =
            entitySocialResponses?.filter((item) => socialResponseIds.includes(item.id)) ?? [];
          dispatch({
            type: EntityContextActionType.UPDATE_USER_ENTITY_SOCIAL_RESPONSES,
            userEntitySocialResponses: newUserSocialResponses,
          });
        },
        updateEntityPendingTasks: async () => {
          if (!authState.session?.selectedAffiliation || !canReadPlanner) {
            dispatch({
              type: EntityContextActionType.UPDATE_ENTITY_PENDING_TASKS,
              pendingTasks: [],
            });
            setHidePending(true);
            return;
          }

          try {
            const response = await EntityPendingTasksRepository.instance.getPendingTasks(
              authState.session.selectedAffiliation.entity.id,
              20,
              0
            );

            dispatch({
              type: EntityContextActionType.UPDATE_ENTITY_PENDING_TASKS,
              pendingTasks: response.data,
            });

            if (response.totalCount > 0 && response.data.length > 0) {
              setShowPending(true);
            } else {
              setHidePending(true);
            }
          } catch (error) {
            console.debug('🔴 Could not fetch entity pending tasks.', error);
            dispatch({
              type: EntityContextActionType.UPDATE_ENTITY_PENDING_TASKS,
              pendingTasks: [],
            });
            setHidePending(true);
          }
        },
        updateEntityOccurrencesCount: async () => {
          if (!authState.session?.selectedAffiliation) return;

          await EntityOccurrencesRepository.instance
            .getOccurrencesCount(authState.session.selectedAffiliation.entity.id)
            .then((response) => {
              console.debug('🔄 Occurrences badge count updated in to', response);
              dispatch({
                type: EntityContextActionType.UPDATE_ENTITY_OCCURRENCES_COUNT,
                occurrencesCount: response,
              });
            });
        },
        updateEntityOccurrencesTypes: async () => {
          if (!authState.session?.selectedAffiliation) return;

          await EntityOccurrencesRepository.instance
            .getOccurrencesTypes(authState.session.selectedAffiliation.entity.id)
            .then((response) => {
              dispatch({
                type: EntityContextActionType.UPDATE_ENTITY_OCCURRENCES_TYPES,
                occurrencesTypes: response,
              });
            });
        },
      }}
    >
      {children}
    </EntityContext.Provider>
  );
};
